import { useContext, useMemo, useState } from 'react'

import { CancelLinkButton } from '@ors/components/ui/Button/Button'
import Dropdown from '@ors/components/ui/Dropdown/Dropdown'
import EditActionModals from './EditActionModals'
import {
  DropDownButtonProps,
  DropDownMenuProps,
  IncreaseVersionButton,
} from '../HelperComponents'
import {
  dropDownClassName,
  dropdownItemClassname,
  enabledButtonClassname,
  projectPhaseOutFields,
} from '../constants'
import {
  checkInvalidValue,
  formatApprovalData,
  formatFiles,
  formatProjectFields,
  formatSubmitData,
  getActualData,
  getApprovalErrors,
  getCrossCuttingErrors,
  getDefaultImpactErrors,
  getHasNoFiles,
  getNonFieldErrors,
  getPostExcomApprovalErrors,
  getPostExcomMeetingErrors,
  getSpecificFieldsErrors,
  hasSectionErrors,
  hasSpecificField,
  isOtherOdsReplacement,
} from '../utils'
import {
  ProjectFile,
  ProjectTypeApi,
  ActionButtons,
  RelatedProjectsType,
  ProjectSpecificFields,
  FileMetaDataType,
} from '../interfaces'
import { useUpdatedFields } from '@ors/contexts/Projects/UpdatedFieldsContext'
import ProjectsDataContext from '@ors/contexts/Projects/ProjectsDataContext'
import PermissionsContext from '@ors/contexts/PermissionsContext'
import { api, uploadFiles } from '@ors/helpers'
import { useStore } from '@ors/store'

import { filter, find, fromPairs, lowerCase, map, pick } from 'lodash'
import { Button, Divider } from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import { useLocation } from 'wouter'
import cx from 'classnames'
import dayjs from 'dayjs'

const EditActionButtons = ({
  projectData,
  setProjectData,
  project,
  files,
  projectFiles,
  setProjectTitle,
  isSaveDisabled,
  isSubmitDisabled,
  setIsLoading,
  setFileErrors,
  setErrors,
  setProjectFiles,
  specificFields,
  trancheErrors,
  getTrancheErrors,
  approvalFields = [],
  specificFieldsLoaded,
  postExComUpdate,
  filesMetaData,
  shouldValidateTotalFund,
}: ActionButtons & {
  setProjectTitle: (title: string) => void
  project: ProjectTypeApi
  isSubmitDisabled: boolean
  projectFiles?: ProjectFile[]
  setProjectFiles: (value: ProjectFile[]) => void
  approvalFields?: ProjectSpecificFields[]
  postExComUpdate?: boolean
  shouldValidateTotalFund: boolean
}) => {
  const [_, setLocation] = useLocation()
  const { setInlineMessage } = useStore((state) => state.inlineMessage)

  const {
    canUpdateProjects,
    canUpdateV3Projects,
    canSubmitProjects,
    canRecommendProjects,
    canApproveProjects,
    canEditApprovedProjects,
  } = useContext(PermissionsContext)
  const { altTechs } = useContext(ProjectsDataContext)

  const showSubmitTranchesWarningModal = trancheErrors?.tranchesData?.find(
    (tranche: RelatedProjectsType) => tranche.warnings.length > 0,
  )

  const { defaultMpErrors } = useStore((state) => state.mpData)
  const { projectFields } = useStore((state) => state.projectFields)
  const { updatedFields, clearUpdatedFields } = useUpdatedFields()

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [isComponentModalOpen, setIsComponentModalOpen] = useState(false)
  const [isTrancheWarningOpen, setIsTrancheWarningOpen] = useState(false)
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false)
  const [isSendToDraftModalOpen, setIsSendToDraftModalOpen] = useState(false)
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false)
  const [isRecommendModalOpen, setIsRecommendModalOpen] = useState(false)
  const [approvalModalType, setApprovalModalType] = useState<string | null>(
    null,
  )

  const {
    id,
    submission_status,
    version,
    component,
    editable,
    editable_for_actual_fields,
  } = project
  const {
    projIdentifiers,
    crossCuttingFields,
    projectSpecificFields,
    approvalFields: approvalData,
  } = projectData

  const canEditProject =
    editable_for_actual_fields ||
    postExComUpdate ||
    (version < 3 && canUpdateProjects) ||
    (version >= 3 && canUpdateV3Projects)

  const specificFieldsAvailable = map(specificFields, 'write_field_name')
  const odsOdpData =
    map(projectSpecificFields?.ods_odp, (field) =>
      pick(field, specificFieldsAvailable),
    ) ?? []

  const submissionStatus = lowerCase(submission_status)
  const isDraft = submissionStatus === 'draft'
  const isSubmitted = submissionStatus === 'submitted'
  const isWithdrawn = submissionStatus === 'withdrawn'
  const isRecommended = submissionStatus === 'recommended'
  const isApproved = submissionStatus === 'approved'
  const isAfterApproval = isApproved || submissionStatus === 'not approved'

  const crossCuttingErrors = useMemo(
    () =>
      getCrossCuttingErrors(
        crossCuttingFields,
        {},
        'edit',
        project,
        false,
        shouldValidateTotalFund,
        !isApproved,
      ),
    [crossCuttingFields, shouldValidateTotalFund],
  )
  const approvalErrors = useMemo(
    () =>
      getApprovalErrors(
        approvalData,
        crossCuttingFields,
        approvalFields,
        {},
        project,
      ),
    [approvalData, crossCuttingFields, approvalFields],
  )
  const postExcomApprovalErrors = useMemo(
    () => getPostExcomApprovalErrors(approvalData, approvalFields, {}, project),
    [approvalData, approvalFields],
  )

  const specificErrors = useMemo(
    () =>
      getSpecificFieldsErrors(
        projectSpecificFields,
        specificFields,
        {},
        'edit',
        canEditApprovedProjects,
        !isApproved,
        project,
      ),
    [projectSpecificFields, project, specificFields],
  )

  const hasOdsOdpFields = find(
    specificFields,
    (field) => field.table === 'ods_odp',
  )

  const hasOdsOdpErrors =
    hasOdsOdpFields &&
    (odsOdpData.some((data, index) =>
      Object.entries(data)
        .filter(([key]) => !projectPhaseOutFields.includes(key))
        .some(([field, value]) => {
          const odsReplacementValue =
            projectSpecificFields?.ods_odp?.[index]?.['ods_replacement']

          const formattedVal =
            field !== 'ods_replacement_text' ||
            isOtherOdsReplacement(altTechs, odsReplacementValue)
              ? value
              : odsReplacementValue

          return checkInvalidValue(formattedVal)
        }),
    ) ||
      odsOdpData.length === 0)

  const {
    Header: headerErrors = {},
    'Substance Details': substanceErrors = {},
    Impact: impactErrors = {},
  } = specificErrors

  const tabErrors =
    hasSectionErrors(crossCuttingErrors) ||
    hasSectionErrors(headerErrors) ||
    hasSectionErrors(substanceErrors)

  const commonErrors =
    tabErrors ||
    hasOdsOdpErrors ||
    (getHasNoFiles(id, files, projectFiles) &&
      (submission_status !== 'Draft' || version === 1) &&
      (version ?? 0) < 3 &&
      !isWithdrawn &&
      (!component || id === component.original_project_id))

  const hasErrors =
    commonErrors ||
    (!isApproved && hasSectionErrors(impactErrors)) ||
    (isRecommended &&
      dayjs(approvalData.date_completion).isBefore(dayjs(), 'day'))

  const defaultImpactErrors = getDefaultImpactErrors(
    projectSpecificFields,
    specificFields,
  )
  const hasImpactErrors = Object.values(defaultImpactErrors).some(
    (errors) => errors.length > 0,
  )

  const disableSubmit = !specificFieldsLoaded || isSubmitDisabled || hasErrors

  const disableUpdateForV3AndWithdrawn =
    editable_for_actual_fields && !editable && !postExComUpdate
      ? hasImpactErrors
      : isApproved
        ? tabErrors || isSaveDisabled
        : disableSubmit

  const hasPostExcomMeetingErrors = getPostExcomMeetingErrors(projIdentifiers)

  const disableUpdate =
    !specificFieldsLoaded ||
    (project.version >= 3 || isWithdrawn
      ? disableUpdateForV3AndWithdrawn
      : isSaveDisabled) ||
    (postExComUpdate &&
      (!(
        projIdentifiers.post_excom_meeting &&
        projIdentifiers.post_excom_decision
      ) ||
        hasPostExcomMeetingErrors ||
        hasSectionErrors(postExcomApprovalErrors)))

  const disableApprovalActions =
    !specificFieldsLoaded ||
    approvalFields.length === 0 ||
    hasOdsOdpErrors ||
    hasSectionErrors(approvalErrors) ||
    crossCuttingErrors?.['total_fund']?.length > 0 ||
    crossCuttingErrors?.['support_cost_psc']?.length > 0

  const disableApprove = hasSectionErrors(defaultMpErrors)

  const { deletedFilesIds = [], newFiles = [] } = files || {}

  const handleErrors = async (error: any, type?: string) => {
    let errors: any = {}

    if (error instanceof Response) {
      const contentType = error.headers.get('content-type') || ''

      if (contentType.includes('application/json')) {
        errors = await error.json()
      } else {
        if (error.status === 413) {
          setFileErrors(
            'Uploaded files are too large. Maximum file size allowed is 170MB.',
          )
        }
        enqueueSnackbar(<>An error occurred. Please try again.</>, {
          variant: 'error',
        })

        return
      }

      if (error.status === 400) {
        setErrors(errors)

        const nonFieldErrors = getNonFieldErrors(errors)
        if (nonFieldErrors.length > 0) {
          setInlineMessage({
            type: 'error',
            errorMessages: nonFieldErrors,
          })
        }

        if (errors?.files ?? errors?.file ?? errors?.metadata) {
          setFileErrors(
            [errors?.files, errors?.file, errors?.metadata]
              .filter(Boolean)
              .join('\n'),
          )
        }

        if (errors?.details) {
          setInlineMessage({
            type: 'error',
            message: errors.details,
          })
        }

        if (type === 'files' && errors?.error) {
          setFileErrors(errors.error)
        }
      }
    }

    enqueueSnackbar(<>An error occurred. Please try again.</>, {
      variant: 'error',
    })
  }

  const editProject = async (navigationPage?: string) => {
    const shouldEditApprovalFields = isRecommended || isAfterApproval

    setIsLoading(true)
    setFileErrors('')
    setErrors({})
    setInlineMessage(null)

    const formattedProjectFields = formatProjectFields(projectFields)
    const actualData = isApproved
      ? getActualData(
          projectData,
          setProjectData,
          specificFields,
          formattedProjectFields,
        )
      : {}

    if (!editable && editable_for_actual_fields && !postExComUpdate) {
      try {
        const result = await api(`api/projects/v2/${id}/edit_actual_fields/`, {
          data: actualData,
          method: 'PUT',
        })

        clearUpdatedFields()
      } catch (error) {
        await handleErrors(error)
      } finally {
        setIsLoading(false)
        return
      }
    }

    const existingFilesMetadata = filter(
      filesMetaData,
      (metadata) => metadata.id,
    )

    const filesForUpdate = filter(
      existingFilesMetadata,
      (metadata: FileMetaDataType) => {
        const crtFile = find(projectFiles, { id: metadata.id }) as ProjectFile
        return crtFile && crtFile.type !== metadata.type
      },
    )

    const newFilesMetadata = filter(filesMetaData, (metadata) => !metadata.id)
    const formattedFilesMetadata = fromPairs(
      map(newFilesMetadata, (file) => [file.name, file.type]),
    )
    const params = { metadata: JSON.stringify(formattedFilesMetadata) }

    const hasTrancheField = hasSpecificField(specificFields, 'tranche')

    if (
      hasTrancheField &&
      (projectData.projectSpecificFields.tranche ?? 0) > 1 &&
      (project.submission_status !== 'Draft' || !!navigationPage)
    ) {
      const trancheErrors = await getTrancheErrors?.()

      if (trancheErrors) {
        setIsLoading(false)
        enqueueSnackbar(<>An error occurred. Please try again.</>, {
          variant: 'error',
        })
        return
      }
    }

    try {
      // Validate files
      if (newFiles.length > 0) {
        await uploadFiles(
          `/api/project/files/validate/`,
          newFiles,
          false,
          'list',
          params,
        )
      }

      // Update project data, this may create a new version
      // so it's important to run before uploading any files
      // or other modifications.
      // The Project ID is preserved.
      const data = formatSubmitData(
        projectData,
        setProjectData,
        specificFields,
        formattedProjectFields,
        altTechs,
      )

      if (postExComUpdate) {
        data['post-excom-update'] = true
        data['adjustment'] = crossCuttingFields.adjustment
        data['interest'] = crossCuttingFields.interest

        // main fields validation before edit
        await api(`api/projects/v2/${id}`, {
          data: { ...data, validate_request: true },
          method: 'PUT',
        })
      }

      // actual fields validation before edit
      if (isApproved) {
        await api(`api/projects/v2/${id}/edit_actual_fields/`, {
          data: { ...actualData, validate_request: true },
          method: 'PUT',
        })
      }

      // approval fields validation before edit
      if (shouldEditApprovalFields) {
        const data = formatApprovalData(
          projectData,
          setProjectData,
          [...specificFields, ...approvalFields],
          formattedProjectFields,
          altTechs,
        )
        await api(`api/projects/v2/${id}/edit_approval_fields/`, {
          data: { ...data, validate_request: true },
          method: 'PUT',
        })
      }

      const result = await api(`api/projects/v2/${id}`, {
        data: data,
        method: 'PUT',
      })

      // Upload files
      if (newFiles.length > 0) {
        await uploadFiles(
          `/api/projects/v2/${id}/project-files/`,
          newFiles,
          false,
          'list',
          params,
        )
      }

      // Delete files
      if (deletedFilesIds.length > 0) {
        try {
          await api(`/api/projects/v2/${id}/project-files/delete`, {
            data: {
              file_ids: deletedFilesIds,
            },
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'DELETE',
          })
        } catch (error) {
          await handleErrors(error, 'files')
          throw error
        }
      }

      await Promise.all(
        map(filesForUpdate, async (file: FileMetaDataType) => {
          try {
            await api(
              `/api/projects/v2/${id}/project-files/${file.id}/edit_type`,
              {
                data: {
                  file_type: file.type,
                },
                method: 'PUT',
              },
            )
          } catch (error) {
            await handleErrors(error, 'files')
            throw error
          }
        }),
      )

      try {
        const res = await api(
          `/api/projects/v2/${id}/project-files/include_previous_versions`,
          {
            withStoreCache: false,
          },
          false,
        )
        setProjectFiles(formatFiles(res, id))
      } catch (error) {
        enqueueSnackbar(<>Could not fetch updated files.</>, {
          variant: 'error',
        })
        return
      }

      if (isApproved) {
        await api(`api/projects/v2/${id}/edit_actual_fields/`, {
          data: actualData,
          method: 'PUT',
        })
      }

      setProjectTitle(result.title)

      if (navigationPage) {
        setLocation(`/projects-listing/${id}/${navigationPage}`)
      }

      if (shouldEditApprovalFields) {
        const canEditApprovalFields = await editApprovalFields()

        if (!canEditApprovalFields) {
          return
        }
      }

      setInlineMessage({
        type: 'success',
        message: 'Updated project successfully.',
        redirectMessage: 'View project.',
        hrefRedirect: `/projects-listing/${result.id}`,
      })

      if (postExComUpdate) {
        enqueueSnackbar(<>Post ExCom update was successful.</>, {
          variant: 'success',
        })
        setLocation('/projects-listing/listing')
      }

      clearUpdatedFields()

      return true
    } catch (error) {
      await handleErrors(error)
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const onEditProject = async () => {
    const updatedSuccessfully = await editProject()

    if (updatedSuccessfully && !postExComUpdate) {
      enqueueSnackbar(<>Updated project successfully.</>, {
        variant: 'success',
      })
    }
  }

  const onSubmitProject = () => {
    if (showSubmitTranchesWarningModal) {
      setIsTrancheWarningOpen(true)
    } else {
      setIsSubmitModalOpen(true)
    }
  }

  const onCancel = () => {
    if (updatedFields.size > 0) {
      setIsCancelModalOpen(true)
    } else {
      setLocation('/projects-listing/listing')
    }
  }

  const onSendBackToDraftProject = () => {
    setIsSendToDraftModalOpen(true)
  }

  const onWithdrawProject = () => {
    setIsWithdrawModalOpen(true)
  }

  const onRecommendProject = () => {
    setIsRecommendModalOpen(true)
  }

  const onApproveRejectProject = (action: string) => {
    setApprovalModalType(action)
  }

  const sendProjectBackToDraft = async () => {
    const canSendBackToDraft = await editProject()

    if (canSendBackToDraft) {
      try {
        await api(`api/projects/v2/${id}/send_back_to_draft/`, {
          method: 'POST',
        })
        setLocation(`/projects-listing/${id}`)
        enqueueSnackbar(<>Project(s) sent back to draft successfully.</>, {
          variant: 'success',
        })
      } catch (error) {
        enqueueSnackbar(
          <>Could not send project(s) back to draft. Please try again.</>,
          {
            variant: 'error',
          },
        )
      }
    }
    setIsSendToDraftModalOpen(false)
  }

  const withdrawProject = async () => {
    const canWithdraw = await editProject()

    if (canWithdraw) {
      try {
        await api(`api/projects/v2/${id}/withdraw/`, {
          method: 'POST',
        })
        setLocation(`/projects-listing/${id}`)

        enqueueSnackbar(<>Project withdrawn successfully.</>, {
          variant: 'success',
        })
      } catch (error) {
        enqueueSnackbar(<>Could not withdraw project. Please try again.</>, {
          variant: 'error',
        })
      }
    }
    setIsWithdrawModalOpen(false)
  }

  const editApprovalFields = async () => {
    setIsLoading(true)
    setErrors({})
    setInlineMessage(null)

    try {
      const data = formatApprovalData(
        projectData,
        setProjectData,
        [...specificFields, ...approvalFields],
        formatProjectFields(projectFields),
        altTechs,
      )
      const result = await api(`api/projects/v2/${id}/edit_approval_fields/`, {
        data: data,
        method: 'PUT',
      })

      clearUpdatedFields()

      return true
    } catch (error) {
      await handleErrors(error)
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const approveRejectProject = async (action: string) => {
    const updatedSuccessfully = await editProject()

    if (updatedSuccessfully) {
      try {
        await api(`api/projects/v2/${id}/${action}/`, {
          method: 'POST',
        })

        const actionType = action === 'approve' ? 'approved' : 'not approved'

        enqueueSnackbar(<>Project {actionType} successfully.</>, {
          variant: 'success',
        })
        setLocation(`/projects-listing/${id}`)
      } catch (error) {
        await handleErrors(error)
      }
    }

    setApprovalModalType(null)
  }

  return (
    <div className="container flex w-full flex-wrap justify-end gap-x-3 gap-y-2 px-0">
      <CancelLinkButton title="Cancel" href={null} onClick={onCancel} />
      {canEditProject && (
        <Button
          className={cx('px-4 py-2 shadow-none', {
            [enabledButtonClassname]: !disableUpdate,
          })}
          size="large"
          variant="contained"
          onClick={onEditProject}
          disabled={disableUpdate}
        >
          Update project
        </Button>
      )}
      {canUpdateProjects && (isDraft || isSubmitted || isRecommended) && (
        <Button
          className={cx('px-4 py-2 shadow-none', enabledButtonClassname)}
          size="large"
          variant="contained"
          onClick={() => setIsComponentModalOpen(true)}
        >
          Add additional component
        </Button>
      )}
      {canSubmitProjects && isDraft && (
        <IncreaseVersionButton
          title="Submit project"
          onSubmit={onSubmitProject}
          isDisabled={disableSubmit}
        />
      )}
      {canRecommendProjects && isSubmitted && (
        <Dropdown
          className={dropDownClassName}
          ButtonProps={DropDownButtonProps}
          MenuProps={DropDownMenuProps}
          label={<>Approval</>}
        >
          <Dropdown.Item
            disabled={disableSubmit}
            className={cx(dropdownItemClassname, 'text-primary')}
            onClick={onRecommendProject}
          >
            Recommend project
          </Dropdown.Item>
          <Divider className="m-0" />
          <Dropdown.Item
            disabled={disableUpdate}
            className={cx(dropdownItemClassname, 'text-red-900')}
            onClick={onSendBackToDraftProject}
          >
            Send project back to draft
          </Dropdown.Item>
          <Divider className="m-0" />
          <Dropdown.Item
            disabled={disableUpdate}
            className={cx(dropdownItemClassname, 'text-red-900')}
            onClick={onWithdrawProject}
          >
            Withdraw project
          </Dropdown.Item>
        </Dropdown>
      )}
      {canApproveProjects && isRecommended && (
        <Dropdown
          className={dropDownClassName}
          ButtonProps={DropDownButtonProps}
          MenuProps={DropDownMenuProps}
          label={<>Approval</>}
        >
          <Dropdown.Item
            disabled={disableApprovalActions || disableApprove}
            className={cx(dropdownItemClassname, 'text-primary')}
            onClick={() => onApproveRejectProject('approve')}
          >
            Approve project
          </Dropdown.Item>
          <Divider className="m-0" />
          <Dropdown.Item
            disabled={disableApprovalActions}
            className={cx(dropdownItemClassname, 'text-red-900')}
            onClick={() => onApproveRejectProject('reject')}
          >
            Not approve project
          </Dropdown.Item>
        </Dropdown>
      )}
      <EditActionModals
        {...{
          id,
          isCancelModalOpen,
          setIsCancelModalOpen,
          isComponentModalOpen,
          setIsComponentModalOpen,
          isSubmitModalOpen,
          setIsSubmitModalOpen,
          isRecommendModalOpen,
          setIsRecommendModalOpen,
          isWithdrawModalOpen,
          setIsWithdrawModalOpen,
          isSendToDraftModalOpen,
          setIsSendToDraftModalOpen,
          approvalModalType,
          setApprovalModalType,
          setIsTrancheWarningOpen,
          editProject,
          withdrawProject,
          sendProjectBackToDraft,
          approveRejectProject,
        }}
        isTrancheWarningOpen={
          !!showSubmitTranchesWarningModal && isTrancheWarningOpen
        }
      />
    </div>
  )
}

export default EditActionButtons
